<template>
  <div class="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-8">
    <div class="bg-white rounded-lg shadow-lg p-6">
      <h1 class="text-2xl font-bold text-gray-900 mb-6">Checkout Link Generator</h1>
      
      <!-- Locale Selection -->
      <div class="mb-6">
        <label class="block text-sm font-medium text-gray-700 mb-2">
          Language / Locale
        </label>
        <select 
          v-model="selectedLocale" 
          class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500">
          <option value="nl">Nederlands (nl)</option>
          <option value="en">English (en)</option>
        </select>
      </div>

      <!-- Package Selection -->
      <div class="mb-6">
        <label class="block text-sm font-medium text-gray-700 mb-4">
          Select Packages
        </label>
        
        <!-- Group packages by their groupKey for better organization -->
        <div v-for="group in packageGroups" :key="group.key" class="mb-6">
          <h3 class="text-lg font-semibold text-gray-800 mb-3">{{ getGroupDisplayName(group.key) }}</h3>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div 
              v-for="plan in group.packages" 
              :key="plan.id"
              class="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
              <input
                :id="plan.id"
                v-model="selectedPackages"
                :value="plan.id"
                type="checkbox"
                class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded">
              <label :for="plan.id" class="ml-3 flex-1">
                <div class="text-sm font-medium text-gray-900">
                  {{ getDisplayName(plan.displayName) }}
                </div>
                <div class="text-xs text-gray-500">ID: {{ plan.id }}</div>
                <div class="text-xs text-gray-500">WHMCS: {{ plan.whmcsProductId }}</div>
              </label>
            </div>
          </div>
        </div>
      </div>

      <!-- Billing Interval Selection -->
      <div class="mb-6">
        <label class="block text-sm font-medium text-gray-700 mb-2">
          Billing Interval
        </label>
        <select 
          v-model="selectedInterval" 
          class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500">
          <option value="monthly">Monthly</option>
          <option value="yearly">Yearly</option>
        </select>
      </div>

      <!-- Domain Input -->
      <div class="mb-6">
        <label class="block text-sm font-medium text-gray-700 mb-2">
          Domain (Optional)
        </label>
        <input
          v-model="domainInput"
          type="text"
          placeholder="example.com"
          class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500">
        <p class="text-xs text-gray-500 mt-1">Leave empty for no pre-selected domain</p>
      </div>

      <!-- Quick Select Buttons -->
      <div class="mb-6">
        <label class="block text-sm font-medium text-gray-700 mb-2">
          Quick Select
        </label>
        <div class="flex flex-wrap gap-2">
          <button
            @click="selectedPackages = []"
            class="px-3 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300">
            Clear All
          </button>
          <button
            @click="selectDefaultPackages"
            class="px-3 py-1 text-xs bg-blue-200 text-blue-700 rounded hover:bg-blue-300">
            Default (Total + Domain + Mini Brand)
          </button>
          <button
            @click="selectEssentialPackages"
            class="px-3 py-1 text-xs bg-green-200 text-green-700 rounded hover:bg-green-300">
            Essential (Total + Domain)
          </button>
        </div>
      </div>

      <!-- Generated Link -->
      <div class="mb-6">
        <label class="block text-sm font-medium text-gray-700 mb-2">
          Generated Checkout Link
        </label>
        <div class="relative">
          <textarea
            :value="generatedLink"
            readonly
            rows="4"
            class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 text-sm font-mono"
            placeholder="Select packages to generate link..."></textarea>
          <button
            v-if="generatedLink"
            @click="copyToClipboard"
            class="absolute top-2 right-2 px-2 py-1 text-xs bg-indigo-500 text-white rounded hover:bg-indigo-600">
            {{ copyButtonText }}
          </button>
        </div>
      </div>

      <!-- Preview Link -->
      <div v-if="generatedLink" class="mb-6">
        <a 
          :href="generatedLink"
          target="_blank"
          class="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors">
          <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-2M14 4h6m0 0v6m0-6L10 14"></path>
          </svg>
          Test Link (Opens in New Tab)
        </a>
      </div>

      <!-- Selected Packages Summary -->
      <div v-if="selectedPackages.length > 0" class="bg-gray-50 p-4 rounded-lg">
        <h3 class="text-sm font-medium text-gray-700 mb-2">Selected Packages ({{ selectedPackages.length }})</h3>
        <div class="flex flex-wrap gap-1">
          <span 
            v-for="packageId in selectedPackages" 
            :key="packageId"
            class="inline-flex items-center px-2 py-1 text-xs bg-indigo-100 text-indigo-700 rounded">
            {{ packageId }}
            <button
              @click="selectedPackages = selectedPackages.filter(p => p !== packageId)"
              class="ml-1 text-indigo-500 hover:text-indigo-700">
              ×
            </button>
          </span>
        </div>
      </div>

      <!-- URL Parameters Info -->
      <div class="mt-6 bg-blue-50 p-4 rounded-lg">
        <h3 class="text-sm font-medium text-blue-800 mb-2">URL Parameters Used:</h3>
        <ul class="text-xs text-blue-700 space-y-1">
          <li><code>plans</code>: Selected package IDs (comma-separated)</li>
          <li><code>lang</code>: Language/locale (nl or en)</li>
          <li><code>interval</code>: Billing interval (monthly or yearly)</li>
          <li><code>domain</code>: Pre-selected domain (if provided)</li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { getPlanList } from '@@@/planCatalog/planCatalog';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const selectedLocale = ref<'nl' | 'en'>('nl');
const selectedPackages = ref<string[]>([]);
const selectedInterval = ref<'monthly' | 'yearly'>('monthly');
const domainInput = ref<string>('');
const copyButtonText = ref('Copy');

const allPackages = ref(getPlanList());

// Group packages by their groupKey for better organization
const packageGroups = computed(() => {
  const groups: Record<string, any> = {};
  
  allPackages.value.forEach(pkg => {
    if (!groups[pkg.groupKey]) {
      groups[pkg.groupKey] = {
        key: pkg.groupKey,
        packages: []
      };
    }
    groups[pkg.groupKey].packages.push(pkg);
  });
  
  return Object.values(groups);
});

const generatedLink = computed(() => {
  if (selectedPackages.value.length === 0) return '';
  
  const baseUrl = window.location.origin;
  const plans = selectedPackages.value.join(',');
  const locale = selectedLocale.value;
  const interval = selectedInterval.value;
  const domain = domainInput.value.trim();
  
  let url = `${baseUrl}/checkout?plans=${encodeURIComponent(plans)}&lang=${locale}&interval=${interval}`;
  
  if (domain) {
    url += `&domain=${encodeURIComponent(domain)}`;
  }
  
  return url;
});

function getGroupDisplayName(groupKey: string): string {
  const groupNames: Record<string, string> = {
    buildLaunch: 'Build & Launch',
    techConcierge: 'Tech Concierge',
    growthOptimize: 'Growth & Optimize',
    visualIdentity: 'Visual Identity',
    domain: 'Domain Services'
  };
  return groupNames[groupKey] || groupKey;
}

function getDisplayName(displayNameKey: string): string {
  // Try to get translated name, fallback to key
  try {
    return t(displayNameKey);
  } catch {
    return displayNameKey;
  }
}

function selectDefaultPackages() {
  selectedPackages.value = ['webHosting_total', 'domain_managed', 'branding_mini'];
}

function selectEssentialPackages() {
  selectedPackages.value = ['webHosting_total', 'domain_managed'];
}

async function copyToClipboard() {
  try {
    await navigator.clipboard.writeText(generatedLink.value);
    copyButtonText.value = 'Copied!';
    setTimeout(() => {
      copyButtonText.value = 'Copy';
    }, 2000);
  } catch (err) {
    console.error('Failed to copy:', err);
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = generatedLink.value;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
    copyButtonText.value = 'Copied!';
    setTimeout(() => {
      copyButtonText.value = 'Copy';
    }, 2000);
  }
}

onMounted(() => {
  // Set default locale based on current locale
  const currentLocale = window.location.pathname.includes('/en') ? 'en' : 'nl';
  selectedLocale.value = currentLocale as 'nl' | 'en';
});
</script>